import {
  useDeleteProfileMutation,
  useGetProfileDetailsQuery,
  useGetProfileVisitHistoryQuery,
  usePostProfileMutation,
  useUpdateProfileMutation,
} from '@api/front-office/profile.api.rtk';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AutoGraph,
  Business,
  EmailOutlined,
  HistoryOutlined,
  ListAlt,
  NoAccounts,
  PersonPinOutlined,
  PhoneInTalkOutlined,
  ReportProblem,
  Search,
  TrackChangesOutlined,
} from '@mui/icons-material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  Tab,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { GridColDef } from '@mui/x-data-grid';
import { SHA512 } from 'crypto-js';
import { memo, useEffect, useMemo, useState } from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useDeleteNetLockMutation } from '../../../../api/front-office/netlock.api.rtk';
import DrawerWrapper from '../../../../components/drawer/drawer-wrapper';
import AutoCompleteController from '../../../../components/form/controller/autocomplete.controller';
import InputFieldsController from '../../../../components/form/controller/input-fields.controller';
import CustomNumberFormat, {
  CustomNumberFormatProps,
} from '../../../../components/form/custom-number-format';
import GroupTitle from '../../../../components/form/group-title';
import { DefaultSwitch } from '../../../../components/switch/default-switch';
import DataTable from '../../../../components/table/datatable';
import TracesListModal from '../../../../components/traces/traces-list-modal';
import ContainerTitle from '../../../../components/ui/container-title';
import useMenu from '../../../../hooks/useMenu';
import useToast from '../../../../hooks/useToast';
import { RootState, useAppSelector } from '../../../../store';
import FOFormSidebarBtn from '../../components/fo-form_sidebar-btn';
import { ContactsPersons } from '../../interface/fo-profile-interface';
import { profileGroupSchema } from '../../interface/fo-profile-schema';
import ContactNumberListModal from './contact-details/fo-contact-number-list-modal';
import EmailListModal from './contact-details/fo-email-list-modal';
import ProfileLogsModal from './fo-profile-logs-modal';
import ProfileTakePhotoModal from './fo-profile-take-photo-modal';
import ProfileVisitHistoryModal from './fo-profile-visit-history-modal';

import FOProfileAvatar from './fo-profile-avatar';

import { useGetFilesQuery } from '@api/files.api.rtk';
import useConfirmationDialog from '@hooks/useConfirmationDialog';
import { UploadFileParams } from '@interfaces/files.interface';
import { isSaveDisabled } from '@utils/form';
import { titleCase } from 'title-case';
import { getColor } from '../../../../assets/theme';
import useAppContext from '../../../../hooks/useAppContext';
import useDisclosure from '../../../../hooks/useDisclosure';
import useInformationDialog from '../../../../hooks/useInformationDialog';
import useS3Bucket from '../../../../hooks/useS3Bucket';
import FORoomPostingContainer from '../../containers/fo-room_posting.container';
import { CustomWidthTooltip } from './fo-profile-individual-drawer';
import { handleKeyDown, handlePaste } from '@utils/keypress';
import { CustomMaskFormat } from '@components/form/custom-mask-format';
import { useCurrentModule } from '@hooks/useCurrentModule';
import { isValidEmailByProvider } from '@utils/email-format';

export interface CompanyProfile {
  companyName: string;
  contactNumber?: string;
  primaryEmail?: string;
  marketSegment?: string | null | '';
  businessSource?: string | null | '';
  sms: boolean;
  email: boolean;
  vipLevel?: { vip_level: string } | null;
  accountOwner?: string | null;
  homeAddress?: string | null;
  cityAddress?: string | null;
  country: { code: string; caption: string } | null;
  hotel: boolean;
  pos: boolean;
  banquet: boolean;
  blacklisted: boolean;
  accountMapping?: string | null;
  vatType?: string | null;
  creditLimit?: number | null;
  creditTerms?: number | null;
  guestRemarks?: string | null;
  tinNo?: string | null;
  taxCode?: string | null;
  businessStyle?: string | null;
  salesToGov: boolean;
}

const ContactPersonColumns: GridColDef[] = [
  {
    field: 'profileID',
    headerName: 'Profile ID',
    minWidth: 100,
    flex: 1,
    hideable: false,
    sortable: true,
  },
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 100,
    flex: 1,
    hideable: false,
    sortable: true,
  },
  {
    field: 'contact',
    headerName: 'Contact Number',
    minWidth: 50,
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'email',
    headerName: 'Email',
    minWidth: 50,
    flex: 1,
    sortable: false,
  },
];

const visibleColumns = ContactPersonColumns.filter(
  (column) => !['profileID'].includes(column.field),
);

const FOProfileGroupDrawer = ({
  isOpen,
  onClose,
  profileID,
}: {
  isOpen: boolean;
  onClose: () => void;
  profileID?: string | null | undefined;
}) => {
  const { paperBg } = getColor();
  const { uploadFile, getFile, encryptFile } = useS3Bucket();
  const [fileData, setFileData] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [statusUploadLevel, setStatusUploadLevel] = useState<number>(0);

  const selectedPropertyID = useSelector(
    (state: RootState) => state.properties.selectedProperty.value,
  );
  const optionsVATType = ['VATable', 'VAT Exempt', 'Non VAT', 'Zero Rated'];
  const [isModalOpenContact, setIsModalOpenContact] = useState(false);
  const [isModalOpenEmail, setIsModalOpenEmail] = useState(false);
  const [isModalOpenTakePhoto, setIsModalOpenTakePhoto] = useState(false);
  const [isModalOpenVisitHistory, setIsModalOpenVisitHistory] = useState(false);
  const [isModalOpenLogs, setIsModalOpenLogs] = useState(false);

  const [guestBalance, setGuestBalance] = useState<string>('0.00');
  const [chartOfAccountID, setChartOfAccountID] = useState<number>(0);

  const { gray } = getColor();
  const { toast } = useToast();
  const { handleClose } = useMenu();
  const currentModule = useCurrentModule();

  const { openSecCodeDialog, isSecuCodeCancel } = useAppContext();
  const { DialogInformation, onShowInfoDialog } = useInformationDialog();

  const roomPostingDisclosure = useDisclosure();
  const netlockState = useDisclosure();
  const tracesDisclosure = useDisclosure();
  const deleteProfileDisclosure = useDisclosure();

  const [tabValue, setTabValue] = useState('guest_remarks');
  const [taxCodeId, setTaxCodeId] = useState<number>(0);

  const [fileEncryptedData, setFileEncryptedData] = useState<
    UploadFileParams[]
  >([]);
  const [fileEncryptedString, setFileEncryptedString] = useState<string>('');

  const [isPhotoExisting, setIsPhotoExisting] = useState(false);
  const [fetchFileName, setFetchFileName] = useState<string>('');
  const [triggerFileLoading, setTriggerFileLoading] = useState(false); // used to trigger loading of file

  const [contactPersonData, setContactPersonDataTable] = useState<
    ContactsPersons[]
  >([]);

  const [chartOfAccountOptions, setChartOfAccountOptions] = useState<string[]>(
    [],
  );

  const dataBookingOption = useAppSelector(
    (state) => state.foBookingOptionList,
  );

  const [taxCodeList, setTaxCodeList] = useState<string[]>([]);

  const [createProfile, { isLoading: isLoadingCreate }] =
    usePostProfileMutation();
  const [updateProfile, { isLoading: isLoadingUpdate }] =
    useUpdateProfileMutation();

  const { currentData: profileData, isFetching: isFetchingDetails } =
    useGetProfileDetailsQuery(profileID, {
      skip: !isOpen || !profileID,
      refetchOnMountOrArgChange: true,
    });

  const { data: uploadedData, isFetching: isFileListLoading } =
    useGetFilesQuery(profileID!, {
      skip: !isOpen || !profileID,
      refetchOnMountOrArgChange: true,
    });

  const [deleteNetLock] = useDeleteNetLockMutation();

  const { property: optionList } = useAppSelector(
    (state) => state.foBookingOptionList,
  );

  const nationalityList = useAppSelector((state) => state.nationalityList);

  const chartOfAccountList = useAppSelector(
    (state) => state.accgChartOfAccount,
  );
  const taxCodeData = useAppSelector((state) => state.accgTaxCode);

  const [initialIsHotel, setInitialIsHotel] = useState(false);
  const [initialIsPOS, setInitialIsPOS] = useState(false);
  const [initialIsBanquet, setInitialIsBanquet] = useState(false);

  const [isHotel, setIsHotel] = useState(false);
  const [isPOS, setIsPOS] = useState(false);
  const [isBanquet, setIsBanquet] = useState(false);

  const handleDrawerClose = async () => {
    setContactPersonDataTable([]);
    setFileUrl('');
    onClose();
    netlockState.onClose();
    profileID &&
      deleteNetLock(`${profileID}/Profile`)
        .unwrap()
        .catch(() =>
          toast({
            severity: 'error',
            message: 'Error deleting of net lock on database.',
          }),
        );
  };

  const handleOpenModalContact = () => {
    setIsModalOpenContact(true);
    handleClose();
  };

  const handleOpenModalEmail = () => {
    setIsModalOpenEmail(true);
    handleClose();
  };

  const handleOpenModalVisitHistory = () => {
    setIsModalOpenVisitHistory(true);
    handleClose();
  };

  const handleOpenModalLogs = () => {
    setIsModalOpenLogs(true);
    handleClose();
  };
  const [deleteProfile] = useDeleteProfileMutation();
  const handleDeleteProfile = async () => {
    openSecCodeDialog(async (result) => {
      if (!result?.data.isValid) {
        return;
      }
      await deleteProfile(profileID)
        .unwrap()
        .then(() => {
          handleDrawerClose();
          toast({
            severity: 'success',
            message: 'Profile deleted successfully.',
          });
        })
        .catch((err) => {
          toast({
            severity: 'error',
            message: err,
          });
        });
    }, 'del_guest_prof');
  };

  const { DialogComponentV2: DeleteProfileDialog } = useConfirmationDialog();

  const { data: profileVisitHistory } = useGetProfileVisitHistoryQuery(
    `${profileID}`,
    {
      skip: !isOpen,
      refetchOnMountOrArgChange: true,
    },
  );

  const hasVisits = (profileVisitHistory?.data?.visits?.length ?? 0) > 0;

  const handleCloseModalContact = () => setIsModalOpenContact(false);
  const handleCloseModalEmail = () => setIsModalOpenEmail(false);
  const handleCloseModalTakePhoto = () => setIsModalOpenTakePhoto(false);
  const handleCloseModalVisitHistory = () => setIsModalOpenVisitHistory(false);
  const handleCloseModalLogs = () => setIsModalOpenLogs(false);

  useEffect(() => {
    if (!isOpen) return;
    if (chartOfAccountList && chartOfAccountList.length > 0) {
      const accountDetailedList = chartOfAccountList.map((account) => ({
        ...account,
      }));

      const mappedOptionsReceivable: string[] = accountDetailedList
        .map((account) => account.accountName)
        .sort((a, b) => a.localeCompare(b));

      setChartOfAccountOptions(mappedOptionsReceivable);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (taxCodeData?.dataResult) {
      const taxCodeList = taxCodeData.dataResult.map(
        (item: { code: string }) => item.code,
      );
      setTaxCodeList(taxCodeList);

      if (!profileID && taxCodeList.length) {
        setValue('taxCode', taxCodeList[0]);
        handleTaxChange(taxCodeList[0]);
      }
    } else {
      toast({
        message: 'Kindly configure Alphanumeric Tax Code',
        severity: 'error',
      });
    }
  }, [isOpen]);

  const handleTaxChange = (code: string) => {
    try {
      const selectedTaxCode = taxCodeData?.dataResult.find(
        (taxCode: { code: string; id: number }) => taxCode.code === code,
      );

      setTaxCodeId(selectedTaxCode?.id || 0);
    } catch (error) {
      console.error('Error selecting tax code:', error);
    }
  };

  const handleChartOfAccountChange = (selectedChartOfAccount: string) => {
    try {
      const findChartOfAccount = chartOfAccountList.find(
        (account) => account.accountName === selectedChartOfAccount,
      );

      if (findChartOfAccount) {
        setChartOfAccountID(findChartOfAccount.id);
      } else {
        console.warn(
          `Chart of Account not found for: ${selectedChartOfAccount}`,
        );
      }
    } catch (error) {
      console.error(
        `Error selecting Chart of Account: ${selectedChartOfAccount}`,
        error,
      );
    }
  };

  useEffect(() => {
    setStatusUploadLevel(0);
    if (!profileID || !isOpen || !profileData?.data.profiles) return;

    try {
      const profiles = profileData.data.profiles;

      profiles.forEach((profileWrapper) => {
        const profile = profileWrapper.profile;
        setValue('companyName', profile.caption);
        setValue('contactNumber', profile.contact);
        setValue('primaryEmail', profile.email);
        setValue('marketSegment', profile.msegment);
        setValue('businessSource', profile.bsource);
        setValue('sms', profile.isSMS);
        setValue('email', profile.isEmail);
        setValue('accountOwner', profile.owner);
        setValue('homeAddress', profile.address);
        setValue('cityAddress', profile.province);
        setValue('country', {
          code: profile.countryCode,
          caption: profile.countryName,
        });
        setValue('hotel', profile.isCLHotel);
        setValue('pos', profile.isCLPOS);
        setValue('banquet', profile.isCLBanquet);

        setInitialIsHotel(profile.isCLHotel);
        setInitialIsPOS(profile.isCLPOS);
        setInitialIsBanquet(profile.isCLBanquet);

        setIsHotel(profile.isCLHotel);
        setIsPOS(profile.isCLPOS);
        setIsBanquet(profile.isCLBanquet);

        setValue('blacklisted', profile.isBlackListed);
        setChartOfAccountID(profile.coaID);
        setValue('accountMapping', profile.accountMapping);
        setValue('vatType', profile.vatType);
        setValue('creditLimit', profile.creditLimit);
        setValue('creditTerms', profile.creditTerms);
        setValue('guestRemarks', profile.remarks);

        setValue('tinNo', profile.tinNo);
        setValue('taxCode', profile.taxCode);
        setValue('businessStyle', profile.taxBusStyle);
        setValue('salesToGov', profile.isSalesToGov);

        setGuestBalance(profile.balance);

        setFetchFileName(profile.fileName);

        setContactPersonDataTable(profile.contactPersons || []);

        if (profile.netLock === 1) {
          toast({
            severity: 'warning',
            message: `${
              profile.lockByUserShortName || 'Someone'
            } is editing this Profile.`,
          });
          netlockState.onOpen();
        }
      });
    } catch (error) {
      console.error('Error fetching profile details:', error);
    }
  }, [profileData, isOpen, profileID]);

  useEffect(() => {
    (async () => {
      if (isFileListLoading) return;
      setIsLoading(true);

      const fileID = uploadedData?.[0]?.fileID; // get the first file ID always

      if (!fileID) {
        if (fetchFileName) {
          setIsPhotoExisting(false);
          setFileUrl('');
        }
        setIsLoading(false);
        return;
      }

      const imageUrl = await getFile(fileID);
      if (imageUrl) {
        setFileUrl(imageUrl);
        setIsPhotoExisting(true);
      } else {
        setFileUrl('');
        setIsPhotoExisting(false);
      }

      setIsLoading(false);
    })();
  }, [uploadedData]);

  useEffect(() => {
    if (isSecuCodeCancel) {
      if (!profileID) {
        setValue('hotel', false);
        setValue('pos', false);
        setValue('banquet', false);

        setIsHotel(false);
        setIsPOS(false);
        setIsBanquet(false);
      } else {
        setValue('hotel', initialIsHotel);
        setValue('pos', initialIsPOS);
        setValue('banquet', initialIsBanquet);

        setIsHotel(initialIsHotel);
        setIsPOS(initialIsPOS);
        setIsBanquet(initialIsPOS);

        setInitialIsHotel(initialIsHotel);
        setInitialIsPOS(initialIsPOS);
        setInitialIsBanquet(initialIsBanquet);
      }
    }
  }, [isSecuCodeCancel]);

  useEffect(() => {
    // set encrypted file data using filename and encrypted data
    if (fileEncryptedString && fileData) {
      setFileEncryptedData([
        {
          fileData,
          fileEncryptedData: fileEncryptedString,
        },
      ]);
    }
  }, [fileEncryptedString]);

  useEffect(() => {
    (async () => {
      if (!fileData) return;
      await encryptFile(fileData, setFileEncryptedString, (level) =>
        setStatusUploadLevel(level),
      );
    })();
  }, [fileData]);

  useEffect(() => {
    if (!triggerFileLoading) {
      return;
    }

    // for upload loader
    if (statusUploadLevel === 100) {
      setIsUploadLoading(false);
      setTriggerFileLoading(false);
      setStatusUploadLevel(0);
    } else {
      setIsUploadLoading(true);
    }
  }, [statusUploadLevel, triggerFileLoading]);

  useEffect(() => {
    if (!isOpen || profileID) return; // Used only for Create Profile

    const firstMarketSegment =
      optionList?.marketSegment?.[0]?.market_segment.ms_code;
    if (!firstMarketSegment) return;

    setValue('marketSegment', firstMarketSegment);

    const matchedSegment = optionList?.marketSegment?.find(
      (item) => item.market_segment.ms_code === firstMarketSegment,
    );

    const firstBusinessSource =
      matchedSegment?.market_segment.ms_options[0]?.business_source?.[0]
        ?.bsource_code;

    if (firstBusinessSource) {
      setValue('businessSource', firstBusinessSource);
    }
  }, [isOpen, optionList]);

  const handleGroupProfile: SubmitHandler<CompanyProfile> = async (data) => {
    if (!profileID) {
      if (isHotel || isPOS || isBanquet) {
        handleShowDialog(data, 'create');
      } else {
        await processProfile(data, 'create');
      }
    } else {
      if (
        isHotel !== initialIsHotel ||
        isPOS !== initialIsPOS ||
        isBanquet !== initialIsBanquet
      ) {
        handleShowDialog(data, 'update');
      } else {
        await processProfile(data, 'update');
      }
    }
  };

  const handleShowDialog = async (
    data: CompanyProfile,
    action: 'create' | 'update',
  ) => {
    onShowInfoDialog({
      title: 'Restricted Feature',
      desc: `A security code is required to enable or disable the City Ledger account feature for this customer account.
            
Please enter a valid security code to continue.`,
      onOk: () => {
        openSecCodeDialog(async (result) => {
          if (result?.data.isValid) {
            await processProfile(data, action);
          } else {
            return;
          }
        }, 'config_cl_accounts');
      },
    });
  };

  const processProfile = async (
    data: CompanyProfile,
    action: 'create' | 'update',
  ) => {
    const {
      companyName,
      contactNumber,
      primaryEmail,
      marketSegment,
      businessSource,
      sms,
      email,
      vipLevel,
      accountOwner,
      homeAddress,
      cityAddress,
      country,
      hotel,
      pos,
      banquet,
      blacklisted,
      vatType,
      creditLimit,
      creditTerms,
      guestRemarks,
      tinNo,
      businessStyle,
      salesToGov,
    } = data;

    const profileType = 'Company';
    const titleCaseCompanyName = titleCase(companyName);
    const vip = (vipLevel?.vip_level?.split(' ')?.[1] || 0) ?? '';
    const owner = accountOwner ?? '';
    const coaId = chartOfAccountID ?? 0;
    const address = homeAddress ?? '';
    const city = cityAddress ?? '';
    const profileCountry = String(country?.code || '');
    const vat = vatType ?? '';
    const profileBusinessSource = businessSource ?? '';

    const signature = SHA512(
      selectedPropertyID +
        (action === 'update' ? profileID : '') +
        profileType +
        fileName +
        titleCaseCompanyName +
        contactNumber +
        primaryEmail +
        marketSegment +
        profileBusinessSource +
        owner +
        address +
        city +
        String(profileCountry) +
        String(vip) +
        creditLimit +
        creditTerms +
        vat +
        coaId +
        guestRemarks +
        +!!sms +
        +!!email +
        +!!blacklisted +
        +!!hotel +
        +!!pos +
        +!!banquet +
        '' +
        '' +
        '' +
        '' +
        '' +
        '' +
        '' +
        '' +
        '' +
        '' +
        '' +
        tinNo +
        taxCodeId +
        businessStyle +
        +!!salesToGov,
    ).toString();

    const company = {
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            profiles: [
              {
                profile: {
                  ...(action === 'update' && { profileID: profileID }),
                  type: profileType,
                  fileName: fileName,
                  caption: titleCaseCompanyName,
                  contact: contactNumber,
                  email: primaryEmail,
                  marketSegment: marketSegment,
                  businessSource: businessSource,
                  isSMS: sms,
                  isEmail: email,
                  owner: owner,
                  address: address,
                  city: city,
                  country: String(profileCountry),
                  vip: String(vip),
                  isBlacklisted: blacklisted,
                  creditLimit: creditLimit,
                  creditTerms: creditTerms,
                  coaID: coaId,
                  vatType: vat,
                  remarks: guestRemarks,
                  isHotel: hotel,
                  isPOS: pos,
                  isBanquet: banquet,
                  tinNo: tinNo,
                  taxCodeId: taxCodeId,
                  businessStyle: businessStyle,
                  isSalesToGov: salesToGov,
                  signature: signature,
                },
              },
            ],
          },
        },
      ],
    };

    try {
      if (action === 'create') {
        const profileReferenceId = await createProfile(company).unwrap();

        if (fileData && profileReferenceId && fileEncryptedData.length > 0) {
          uploadFile(
            profileReferenceId,
            fileEncryptedData,
            'Profile Photo',
            'Profiles',
            true,
          );
        }

        toast({
          severity: 'success',
          message: 'Profile added successfully',
        });
      } else {
        await updateProfile(company).unwrap();

        if (
          fileData &&
          profileID &&
          fileEncryptedData.length > 0 &&
          !isPhotoExisting
        ) {
          uploadFile(
            profileID,
            fileEncryptedData,
            'Profile Photo',
            'Profiles',
            true,
          );
        }

        toast({
          severity: 'success',
          message: 'Changes saved successfully',
        });
      }

      reset();
      handleDrawerClose();
    } catch (err) {
      toast({
        severity: 'error',
        message: err,
      });
    }
  };

  const handleTabChange = (_e: React.SyntheticEvent, newValue: string) => {
    if (typeof newValue !== 'string') return;
    setTabValue(newValue);
  };

  const methods = useForm<CompanyProfile>({
    defaultValues: {
      companyName: '',
      contactNumber: '',
      primaryEmail: '',
      marketSegment: 'FIT',
      businessSource: 'Walk-in',
      sms: true,
      email: true,
      vipLevel: null,
      accountOwner: null,
      homeAddress: '',
      cityAddress: null,
      country: { code: 'PHL', caption: 'Philippines' },
      hotel: false,
      pos: false,
      banquet: false,
      blacklisted: false,
      accountMapping: '',
      vatType: 'VATable',
      creditLimit: 0,
      creditTerms: 90,
      guestRemarks: '',
      tinNo: '',
      taxCode: '',
      businessStyle: '',
      salesToGov: false,
    },
    resolver: yupResolver<CompanyProfile>(profileGroupSchema),
    mode: 'onSubmit',
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = methods;

  return (
    <FormProvider {...methods}>
      <DrawerWrapper
        {...{
          id: 'group-profile-drawer',
          open: isOpen,
          onClose: handleDrawerClose,
          withHeader: {
            title: (
              <Stack direction='row' flexWrap='wrap' gap={2}>
                <ContainerTitle
                  title='Profile'
                  func={() => {}}
                  titleStyle={{ fontSize: '20px!important' }}
                />
                <Stack
                  direction='row'
                  gap={2}
                  sx={{
                    marginLeft: 'auto',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Typography>
                    Outstanding Balance:
                    <Typography
                      component='span'
                      sx={{
                        fontWeight: 550,
                        marginLeft: 1,
                        fontSize: '20px',
                        color: '#2B3493',
                      }}
                    >
                      {guestBalance}
                    </Typography>
                  </Typography>
                </Stack>
              </Stack>
            ),
            learnMore: () => {},
          },
          isLoading: isLoadingCreate || isLoadingUpdate || isFetchingDetails,
        }}
      >
        {/* MAIN */}
        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          sx={{
            opacity: netlockState.isOpen ? 0.5 : 'unset',
            pointerEvents: netlockState.isOpen ? 'none' : 'unset',
            width: { xs: '100%', lg: '1100px' },
            maxHeight: '100dvh',
            flex: 1,
            overflowY: { xs: 'auto', md: 'hidden' },
            ':hover': {
              overflowY: 'auto',
            },
          }}
        >
          {/* CONTENT */}
          <Stack
            flex={1}
            sx={{
              width: '100%',
            }}
          >
            {/* FORM DETAILS */}
            <Stack
              component='form'
              id='group-profile'
              onSubmit={handleSubmit(handleGroupProfile)}
              sx={{
                flex: 1,
                height: '100%',
                padding: '0 16px',
                overflowY: 'hidden',
                ':hover': {
                  overflowY: { xs: 'hidden', sm: 'auto' },
                },
              }}
            >
              <Grid2
                container
                spacing={3}
                sx={{
                  py: 2,
                }}
              >
                {/* UPLOAD IMAGE */}
                <Grid2 {...{ xs: 12, md: 4 }}>
                  <Stack gap={2}>
                    {/* AVATAR */}
                    <FOProfileAvatar
                      isLoading={isLoading || isUploadLoading}
                      fileUrl={fileUrl}
                      handleUploadPhoto={(fileData, fileUrl, fileName) => {
                        setFileData(fileData);
                        setFileUrl(fileUrl);
                        setFileName(fileName);
                        setIsPhotoExisting(false);
                        setTriggerFileLoading(true);
                      }}
                      handleOpenModalTakePhoto={() => {
                        setIsModalOpenTakePhoto(true);
                        handleClose();
                      }}
                    />
                    {/* ALLOWED ACCOUNTS */}
                    <Stack>
                      <GroupTitle
                        title='Allowed Accounts'
                        wrapperStyle={{ marginBottom: 1 }}
                      />
                      <Stack
                        direction='row'
                        alignItems='center'
                        sx={{
                          p: 1,
                          borderBottom: '1px dashed lightgray',
                        }}
                      >
                        <Typography sx={{ flexGrow: 1 }}>Hotel</Typography>
                        <ThemeProvider theme={DefaultSwitch}>
                          <FormControlLabel
                            control={
                              <Controller
                                name='hotel'
                                control={control}
                                render={({ field }) => (
                                  <Switch
                                    {...field}
                                    checked={field.value}
                                    onChange={(e) => {
                                      field.onChange(e.target.checked);
                                      setIsHotel(e.target.checked);
                                    }}
                                  />
                                )}
                              />
                            }
                            label=''
                            labelPlacement='start'
                          />
                        </ThemeProvider>
                      </Stack>
                      <Stack
                        direction='row'
                        alignItems='center'
                        sx={{
                          p: 1,
                          borderBottom: '1px dashed lightgray',
                        }}
                      >
                        <Typography sx={{ flexGrow: 1 }}>POS</Typography>
                        <ThemeProvider theme={DefaultSwitch}>
                          <FormControlLabel
                            control={
                              <Controller
                                name='pos'
                                control={control}
                                render={({ field }) => (
                                  <Switch
                                    {...field}
                                    checked={field.value}
                                    onChange={(e) => {
                                      field.onChange(e.target.checked);
                                      setIsPOS(e.target.checked);
                                    }}
                                  />
                                )}
                              />
                            }
                            label=''
                            labelPlacement='start'
                          />
                        </ThemeProvider>
                      </Stack>
                      <Stack
                        direction='row'
                        alignItems='center'
                        sx={{
                          p: 1,
                        }}
                      >
                        <Typography sx={{ flexGrow: 1 }}>Banquet</Typography>
                        <ThemeProvider theme={DefaultSwitch}>
                          <FormControlLabel
                            control={
                              <Controller
                                name='banquet'
                                control={control}
                                render={({ field }) => (
                                  <Switch
                                    {...field}
                                    checked={field.value}
                                    onChange={(e) => {
                                      field.onChange(e.target.checked);
                                      setIsBanquet(e.target.checked);
                                    }}
                                  />
                                )}
                              />
                            }
                            label=''
                            labelPlacement='start'
                          />
                        </ThemeProvider>
                      </Stack>
                    </Stack>

                    {/* OTHER */}
                    <Stack>
                      <GroupTitle
                        title='Other'
                        wrapperStyle={{ marginBottom: 1 }}
                      />
                      <Stack
                        direction='row'
                        alignItems='center'
                        sx={{
                          p: 1,
                        }}
                      >
                        <Typography sx={{ flexGrow: 1 }}>
                          Blacklisted
                        </Typography>
                        <ThemeProvider theme={DefaultSwitch}>
                          <FormControlLabel
                            control={
                              <Controller
                                name='blacklisted'
                                control={control}
                                render={({ field }) => (
                                  <Switch {...field} checked={field.value} />
                                )}
                              />
                            }
                            label=''
                            labelPlacement='start'
                          />
                        </ThemeProvider>
                      </Stack>
                    </Stack>

                    <Stack direction='row' alignItems='center'>
                      <AutoCompleteController
                        control={control}
                        errors={errors}
                        label='Account Mapping'
                        name='accountMapping'
                        options={chartOfAccountOptions || []}
                        onChange={handleChartOfAccountChange}
                        borderErrorOnly
                        style={{ margin: 0 }}
                      />
                    </Stack>

                    <Stack direction='row' spacing={2}>
                      <AutoCompleteController
                        control={control}
                        errors={errors}
                        label='Tax Type'
                        name='vatType'
                        options={optionsVATType || []}
                        borderErrorOnly
                        style={{ margin: 0 }}
                      />
                    </Stack>
                    <Stack direction='row' spacing={2}>
                      <InputFieldsController
                        control={control}
                        errors={errors}
                        label='Credit Limit'
                        name='creditLimit'
                        inputProps={{
                          style: { textAlign: 'right' },
                          onFocus: () => true,
                        }}
                        InputProps={{
                          inputComponent: (props: CustomNumberFormatProps) => (
                            <CustomNumberFormat
                              {...props}
                              allowNegative={false}
                              isNegative={false}
                            />
                          ),
                        }}
                        autoComplete='on'
                        borderErrorOnly
                        wrapperStyle={{
                          marginBottom: 0,
                        }}
                      />
                      <AutoCompleteController
                        control={control}
                        errors={errors}
                        label='Credit Terms'
                        name='creditTerms'
                        options={[]}
                        borderErrorOnly
                        style={{ margin: 0 }}
                        textFieldProps={{
                          inputProps: {
                            maxLength: 3,
                          },
                        }}
                        freeSolo
                      />
                    </Stack>
                  </Stack>
                </Grid2>

                {/* PROFILE DETAILS */}
                <Grid2 {...{ xs: 12, md: 8 }}>
                  <Stack gap={2}>
                    {/* PRIMARY DETAILS */}
                    <Box>
                      <Grid2 {...{ xs: 12 }} container gap={1}>
                        <Grid2 {...{ xs: 12 }}>
                          <GroupTitle title='Primary Details' />
                        </Grid2>
                      </Grid2>
                      <Stack gap={2}>
                        <Grid2 container spacing={2}>
                          <Grid2 {...{ xs: 12, md: 12 }}>
                            <InputFieldsController
                              label='Company Name *'
                              name='companyName'
                              control={control}
                              errors={errors}
                              InputProps={{
                                autoComplete: 'new-company',
                              }}
                              borderErrorOnly
                              wrapperStyle={{
                                marginBottom: 0,
                              }}
                              inputProps={{ maxLength: 100 }}
                            />
                          </Grid2>
                        </Grid2>
                      </Stack>
                    </Box>

                    {/* CONTACT DETAILS */}
                    <Box>
                      <Grid2 {...{ xs: 12 }} container gap={1}>
                        <Grid2 {...{ xs: 12 }}>
                          <GroupTitle title='Contact Details' />
                        </Grid2>
                      </Grid2>
                      <Grid2 container spacing={2}>
                        <Grid2 {...{ xs: 12, md: 6 }}>
                          <InputFieldsController
                            label='Contact Number'
                            name='contactNumber'
                            control={control}
                            errors={errors}
                            inputStyle={{
                              '.MuiInputBase-root': { width: '100%' },
                            }}
                            InputProps={{
                              autoComplete: 'new-contact',
                              startAdornment: (
                                <InputAdornment position='start'>
                                  <PhoneInTalkOutlined />
                                </InputAdornment>
                              ),
                              endAdornment: profileID && (
                                <InputAdornment position='end'>
                                  <IconButton
                                    edge='end'
                                    onClick={handleOpenModalContact}
                                    style={{
                                      padding: '6px',
                                      backgroundColor: '#2B3493',
                                      borderRadius: '4px',
                                      color: '#ffffff',
                                    }}
                                  >
                                    <Search />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            borderErrorOnly
                            wrapperStyle={{
                              marginBottom: 0,
                            }}
                            inputProps={{
                              maxLength: 11,
                            }}
                            onPaste={handlePaste}
                            onKeyDown={handleKeyDown}
                          />
                        </Grid2>
                        <Grid2 {...{ xs: 12, md: 6 }}>
                          <InputFieldsController
                            label='Email'
                            name='primaryEmail'
                            control={control}
                            errors={errors}
                            inputStyle={{
                              '.MuiInputBase-root': { width: '100%' },
                            }}
                            InputProps={{
                              autoComplete: 'new-email',
                              startAdornment: (
                                <InputAdornment position='start'>
                                  <EmailOutlined />
                                </InputAdornment>
                              ),
                              endAdornment: profileID && (
                                <InputAdornment position='end'>
                                  <IconButton
                                    edge='end'
                                    onClick={handleOpenModalEmail}
                                    style={{
                                      padding: '8px',
                                      backgroundColor: '#2B3493',
                                      borderRadius: '4px',
                                      color: '#ffffff',
                                    }}
                                  >
                                    <Search />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            onBlur={(e) => {
                              const value = e.target.value;
                              if (value && !isValidEmailByProvider(value)) {
                                toast({
                                  severity: 'warning',
                                  message:
                                    "This email doesn't look valid for its provider — please double check it.",
                                });
                              }
                            }}
                            borderErrorOnly
                            wrapperStyle={{
                              marginBottom: 0,
                            }}
                          />
                        </Grid2>
                      </Grid2>
                    </Box>

                    {/* MARKETING DETAILS */}
                    <Box>
                      <Grid2 {...{ xs: 12 }} container gap={1}>
                        <Grid2 {...{ xs: 12 }}>
                          <GroupTitle title='Marketing Details' />
                        </Grid2>
                      </Grid2>
                      <Stack gap={2}>
                        <Grid2 container spacing={2}>
                          <Grid2 {...{ xs: 12, md: 6 }}>
                            <AutoCompleteController
                              control={control}
                              errors={errors}
                              label='Market Segment'
                              name='marketSegment'
                              options={
                                optionList.marketSegment.map(
                                  (item) => item.market_segment.ms_code,
                                ) || []
                              }
                              style={{ margin: 0, width: '100%' }}
                              textFieldProps={{
                                inputProps: {
                                  maxLength: 20,
                                },
                                InputProps: {
                                  startAdornment: (
                                    <InputAdornment
                                      position='start'
                                      sx={{
                                        width: '24px',
                                        pl: '7px',
                                        pr: '14px',
                                      }}
                                    >
                                      <AutoGraph />
                                    </InputAdornment>
                                  ),
                                },
                              }}
                              borderErrorOnly
                              onChange={() => setValue('businessSource', '')}
                            />
                          </Grid2>
                          <Grid2 {...{ xs: 12, md: 6 }}>
                            <AutoCompleteController
                              control={control}
                              errors={errors}
                              label={
                                !watch('marketSegment')
                                  ? 'Business Source (Select Market Segment First)'
                                  : 'Business Source'
                              }
                              name='businessSource'
                              options={
                                optionList.marketSegment
                                  .find(
                                    (item) =>
                                      item.market_segment.ms_code ==
                                      watch('marketSegment'),
                                  )
                                  ?.market_segment.ms_options[0]?.business_source?.map(
                                    (item) => item.bsource_code,
                                  ) || []
                              }
                              style={{ margin: 0, width: '100%' }}
                              textFieldProps={{
                                inputProps: {
                                  maxLength: 20,
                                },
                                InputProps: {
                                  startAdornment: (
                                    <InputAdornment
                                      position='start'
                                      sx={{
                                        width: '24px',
                                        pl: '7px',
                                        pr: '14px',
                                      }}
                                    >
                                      <Business />
                                    </InputAdornment>
                                  ),
                                },
                              }}
                              borderErrorOnly
                              disabled={!watch('marketSegment')}
                              onChange={(value: string) =>
                                setValue('businessSource', value ?? '')
                              }
                            />
                          </Grid2>
                        </Grid2>
                        <Grid2 container spacing={2}>
                          <Grid2 {...{ xs: 6, md: 2 }}>
                            <Stack
                              direction='row'
                              alignItems='center'
                              height='100%'
                            >
                              <Typography>SMS</Typography>
                              <ThemeProvider theme={DefaultSwitch}>
                                <FormControlLabel
                                  control={
                                    <Controller
                                      name='sms'
                                      control={control}
                                      render={({ field }) => (
                                        <Switch
                                          {...field}
                                          checked={field.value}
                                        />
                                      )}
                                    />
                                  }
                                  label=''
                                  labelPlacement='start'
                                />
                              </ThemeProvider>
                            </Stack>
                          </Grid2>
                          <Grid2 {...{ xs: 6, md: 2 }}>
                            <Stack
                              direction='row'
                              alignItems='center'
                              height='100%'
                            >
                              <Typography>Email</Typography>
                              <ThemeProvider theme={DefaultSwitch}>
                                <FormControlLabel
                                  control={
                                    <Controller
                                      name='email'
                                      control={control}
                                      render={({ field }) => (
                                        <Switch
                                          {...field}
                                          checked={field.value}
                                        />
                                      )}
                                    />
                                  }
                                  label=''
                                  labelPlacement='start'
                                />
                              </ThemeProvider>
                            </Stack>
                          </Grid2>
                          <Grid2 {...{ xs: 12, md: 2 }}>
                            <AutoCompleteController
                              control={control}
                              errors={errors}
                              label='VIP'
                              name='vipLevel'
                              options={
                                dataBookingOption?.property?.VIPLevels || []
                              }
                              sx={{ margin: 0, flex: 1 }}
                              getOptionLabel={(option: any) => option.vip_level}
                              borderErrorOnly
                            />
                          </Grid2>
                          <Grid2 {...{ xs: 12, md: 6 }}>
                            <AutoCompleteController
                              control={control}
                              errors={errors}
                              label='Account Owner'
                              name='accountOwner'
                              options={[]}
                              borderErrorOnly
                              style={{ margin: 0, width: '100%' }}
                              textFieldProps={{
                                inputProps: {
                                  maxLength: 50,
                                },
                              }}
                            />
                          </Grid2>
                        </Grid2>
                      </Stack>
                    </Box>

                    {/* ADDRESS DETAILS */}
                    <Box>
                      <Grid2 {...{ xs: 12 }} container gap={1}>
                        <Grid2 {...{ xs: 12 }}>
                          <GroupTitle title='Address' />
                        </Grid2>
                      </Grid2>
                      <Grid2 container spacing={2}>
                        <Grid2 {...{ xs: 12, md: 6 }}>
                          <InputFieldsController
                            label='Home Address'
                            name='homeAddress'
                            control={control}
                            errors={errors}
                            wrapperStyle={{
                              marginBottom: 0,
                            }}
                            inputProps={{}}
                          />
                        </Grid2>
                        <Grid2 {...{ xs: 12, md: 3 }}>
                          <InputFieldsController
                            control={control}
                            errors={errors}
                            label='City/Province'
                            name='cityAddress'
                            borderErrorOnly
                            wrapperStyle={{
                              marginBottom: 0,
                            }}
                          />
                        </Grid2>
                        <Grid2 {...{ xs: 12, md: 3 }}>
                          <AutoCompleteController
                            control={control}
                            errors={errors}
                            label='Country *'
                            name='country'
                            options={nationalityList || []}
                            getOptionLabel={(option: any) => option.code}
                            borderErrorOnly
                            style={{ margin: 0 }}
                          />
                        </Grid2>
                      </Grid2>
                    </Box>

                    {/* GUEST REMARKS */}
                    <Box>
                      <Grid2 {...{ xs: 12 }} container gap={1}>
                        <Grid2 {...{ xs: 12 }}>
                          <TabContext value={tabValue}>
                            <Stack gap={2}>
                              <TabList onChange={handleTabChange}>
                                <Tab
                                  label='Guest Remarks'
                                  value='guest_remarks'
                                  sx={{
                                    textTransform: 'capitalize',
                                    fontSize: '14px',
                                  }}
                                />
                                <Tab
                                  label='Contact Person'
                                  value='contact_person'
                                  sx={{
                                    textTransform: 'capitalize',
                                    fontSize: '14px',
                                  }}
                                />
                                <Tab
                                  label='Business Info'
                                  value='business_info'
                                  sx={{
                                    textTransform: 'capitalize',
                                    fontSize: '14px',
                                  }}
                                />
                              </TabList>
                              <TabPanel value='guest_remarks' sx={{ p: '0' }}>
                                <Grid2 container spacing={2}>
                                  <Grid2 {...{ xs: 12, md: 12 }}>
                                    <InputFieldsController
                                      label='Guest Remarks'
                                      name='guestRemarks'
                                      placeholder=' '
                                      control={control}
                                      errors={errors}
                                      inputProps={{
                                        minLength: 0,
                                        maxLength: 999,
                                      }}
                                      multiline
                                      rows={3.6}
                                      borderErrorOnly
                                      wrapperStyle={{
                                        marginBottom: 0,
                                      }}
                                    />
                                  </Grid2>
                                </Grid2>
                              </TabPanel>
                              <TabPanel value='contact_person' sx={{ p: '0' }}>
                                <Grid2 container spacing={2}>
                                  <Grid2 {...{ xs: 12, md: 12 }}>
                                    <DataTable
                                      getRowId={(row) => row.profileID}
                                      columns={visibleColumns}
                                      rows={contactPersonData || []}
                                      disableColumnFilter
                                      disableColumnMenu
                                      disableColumnSelector
                                      disableSearch
                                      pageSize={10}
                                      autoPageSize={false}
                                      checkboxSelection={false}
                                      tableStyle={{
                                        padding: 0,
                                        '&.MuiDataGrid-root': {
                                          p: 0,
                                        },
                                        '& .MuiTablePagination-root': {
                                          p: '0 10px',
                                        },
                                        '& .MuiDataGrid-columnHeaders': {
                                          backgroundColor: 'transparent',
                                          borderRadius: 0,
                                        },
                                        ".MuiButtonBase-root[aria-label='Export']":
                                          {
                                            display: 'none!important',
                                          },
                                      }}
                                      loading={isFetchingDetails}
                                      height={200}
                                    />
                                  </Grid2>
                                </Grid2>
                              </TabPanel>
                              <TabPanel
                                value='business_info'
                                sx={{
                                  p: '0',
                                  gap: 2,
                                  display: 'flex',
                                  flexDirection: 'column',
                                }}
                              >
                                <Grid2 container spacing={2}>
                                  <Grid2 {...{ xs: 8, md: 8 }}>
                                    <InputFieldsController
                                      label='Tax Identification Number'
                                      name='tinNo'
                                      placeholder='000-000-0000'
                                      control={control}
                                      errors={errors}
                                      inputProps={{
                                        minLength: 12,
                                        maxLength: 20,
                                      }}
                                      wrapperStyle={{
                                        marginBottom: 0,
                                      }}
                                      InputProps={{
                                        inputComponent: CustomMaskFormat,
                                        inputProps: {
                                          mask: '000-000-000-000000',
                                          definitions: {
                                            '0': /[0-9]/,
                                          },
                                          placeholder: '000-000-000-00000',
                                          overwrite: true,
                                        },
                                      }}
                                    />
                                  </Grid2>
                                  <Grid2 {...{ xs: 4, md: 4 }}>
                                    <AutoCompleteController
                                      control={control}
                                      errors={errors}
                                      label='ATC'
                                      name='taxCode'
                                      options={taxCodeList || []}
                                      onChange={handleTaxChange}
                                      borderErrorOnly
                                      style={{ margin: 0 }}
                                    />
                                  </Grid2>
                                </Grid2>
                                <Grid2 container spacing={2}>
                                  <Grid2 {...{ xs: 8, md: 8 }}>
                                    <InputFieldsController
                                      label='Business Style'
                                      name='businessStyle'
                                      placeholder=''
                                      control={control}
                                      errors={errors}
                                      inputProps={{
                                        maxLength: 100,
                                      }}
                                      wrapperStyle={{
                                        marginBottom: 0,
                                      }}
                                    />
                                  </Grid2>
                                  <Grid2 {...{ xs: 4, md: 4 }} spacing={2}>
                                    <Stack
                                      direction='row'
                                      alignItems='center'
                                      justifyContent='flex-start'
                                      height='100%'
                                      gap={1}
                                    >
                                      <ThemeProvider theme={DefaultSwitch}>
                                        <FormControlLabel
                                          control={
                                            <Controller
                                              name='salesToGov'
                                              control={control}
                                              render={({ field }) => (
                                                <Switch
                                                  {...field}
                                                  checked={field.value}
                                                  sx={{}}
                                                />
                                              )}
                                            />
                                          }
                                          label=''
                                          labelPlacement='start'
                                        />
                                      </ThemeProvider>
                                      <Typography>
                                        Sales To Government
                                      </Typography>
                                    </Stack>
                                  </Grid2>
                                </Grid2>
                              </TabPanel>
                            </Stack>
                          </TabContext>
                        </Grid2>
                      </Grid2>
                    </Box>
                  </Stack>
                </Grid2>
              </Grid2>
            </Stack>

            {/* FORM BUTTON */}
            <Box
              sx={{
                position: 'sticky',
                bottom: '0',
                bgcolor: paperBg,
                zIndex: 20,
              }}
            >
              <Divider
                sx={{
                  borderStyle: 'dashed',
                }}
              />
              <Stack
                sx={{ p: '24px 32px 24px 24px' }}
                direction={{ xs: 'column', sm: 'row' }}
                gap={{ xs: 2, lg: 1 }}
                justifyContent='flex-end'
              >
                <Button
                  variant='outlined'
                  onClick={handleDrawerClose}
                  sx={{
                    width: { xs: '100%', md: '100px' },
                    height: '35px',
                    border: '1px solid',
                    backgroundColor: 'transparent',
                  }}
                >
                  Close
                </Button>
                <Button
                  form='group-profile'
                  onClick={handleSubmit(handleGroupProfile)}
                  variant='contained'
                  sx={{
                    width: { xs: '100%', md: '100px' },
                  }}
                  disabled={
                    isSaveDisabled(errors) || isLoading || isUploadLoading
                  }
                >
                  Save
                </Button>
              </Stack>
            </Box>
          </Stack>

          <Divider
            orientation='vertical'
            flexItem
            sx={{ display: { xs: 'none', sm: 'block' } }}
          />
          <Divider
            orientation='horizontal'
            flexItem
            sx={{ display: { xs: 'block', sm: 'none' } }}
          />

          {/* SIDEBAR BUTTON */}
          <Stack
            direction={{ xs: 'row', sm: 'column' }}
            sx={{
              width: { xs: '100%', sm: '73px' },
              height: { xs: '76px', sm: '100%' },
              p: '12px',
              gap: '12px',
              overflowX: { xs: 'auto', md: 'hidden' },
              minHeight: { xs: '76px', lg: '100%' },
              overflowY: { xs: 'auto', md: 'hidden' },
              ':hover': {
                overflowY: 'auto',
              },
            }}
          >
            <FOFormSidebarBtn
              onClick={roomPostingDisclosure.onOpen}
              icon={PersonPinOutlined}
              title='Account Posting'
              isActive={
                roomPostingDisclosure.isOpen &&
                !isModalOpenVisitHistory &&
                !isModalOpenLogs &&
                !tracesDisclosure.isOpen
              }
              disabled={
                !profileID || guestBalance === '0.00' || currentModule !== 'FO'
              }
            />
            <FOFormSidebarBtn
              onClick={handleOpenModalVisitHistory}
              icon={HistoryOutlined}
              title='Visit History'
              isActive={
                !roomPostingDisclosure.isOpen &&
                isModalOpenVisitHistory &&
                !isModalOpenLogs &&
                !tracesDisclosure.isOpen
              }
              disabled={!profileID}
            />
            <FOFormSidebarBtn
              onClick={tracesDisclosure.onOpen}
              icon={TrackChangesOutlined}
              title='Traces'
              isActive={
                !roomPostingDisclosure.isOpen &&
                !isModalOpenVisitHistory &&
                !isModalOpenLogs &&
                tracesDisclosure.isOpen
              }
              disabled={!profileID}
            />
            <FOFormSidebarBtn
              onClick={handleOpenModalLogs}
              icon={ListAlt}
              title='Logs'
              isActive={
                !roomPostingDisclosure.isOpen &&
                !isModalOpenVisitHistory &&
                isModalOpenLogs &&
                !tracesDisclosure.isOpen
              }
              disabled={!profileID}
            />
            <CustomWidthTooltip
              title={
                !profileID || guestBalance !== '0.00'
                  ? 'All balances must be settled before deleting this profile.'
                  : ''
              }
              placement='left'
              arrow
              sx={{ m: 2 }}
            >
              <span>
                <FOFormSidebarBtn
                  onClick={deleteProfileDisclosure.onOpen}
                  icon={NoAccounts}
                  title={
                    !profileID || guestBalance !== '0.00'
                      ? ''
                      : 'Delete Profile'
                  }
                  isActive={
                    !roomPostingDisclosure.isOpen &&
                    !isModalOpenVisitHistory &&
                    isModalOpenLogs &&
                    !tracesDisclosure.isOpen
                  }
                  // disabled={!profileID || guestBalance !== '0.00'}
                  disabled={true}
                  sxIcon={{
                    color: gray,
                    // !profileID || guestBalance !== '0.00' ? gray : danger,
                  }}
                />
              </span>
            </CustomWidthTooltip>
          </Stack>
        </Stack>
      </DrawerWrapper>

      <>
        {useMemo(
          () => (
            <DeleteProfileDialog
              open={deleteProfileDisclosure.isOpen}
              onConfirm={async () => {
                //Inject print code here
                deleteProfileDisclosure.onClose();
                handleDeleteProfile();
              }}
              desc={
                <Stack gap='8px' sx={{ maxWidth: '405px' }}>
                  <Typography>
                    {hasVisits
                      ? 'This profile is currently in use. Deleting it will result in all associated data being retracted permanently. Are you sure you want to proceed?'
                      : 'Deleting it will result in all associated data being retracted permanently. This action cannot be undone.'}
                  </Typography>
                </Stack>
              }
              title='Are you sure you want to delete this profile?'
              cancelButtonTitle='Cancel'
              confirmButtonTitle='Proceed'
              icon={ReportProblem}
              iconColor={'warning'}
              onClose={() => deleteProfileDisclosure.onClose()}
            />
          ),
          [deleteProfileDisclosure],
        )}
        <ContactNumberListModal
          isOpen={isModalOpenContact}
          onClose={handleCloseModalContact}
          profileID={profileID}
        />

        <EmailListModal
          isOpen={isModalOpenEmail}
          onClose={handleCloseModalEmail}
          profileID={profileID}
        />

        <ProfileTakePhotoModal
          isOpen={isModalOpenTakePhoto}
          onClose={handleCloseModalTakePhoto}
          onCapture={async (fileData, fileUrl, fileName) => {
            setFileData(fileData);
            setFileName(fileName);
            setFileUrl(fileUrl);
            setIsPhotoExisting(false);
            setTriggerFileLoading(true);
          }}
        />

        <ProfileVisitHistoryModal
          isOpen={isModalOpenVisitHistory}
          onClose={handleCloseModalVisitHistory}
          profileID={profileID}
        />

        <ProfileLogsModal
          isOpen={isModalOpenLogs}
          onClose={handleCloseModalLogs}
          profileID={profileID}
        />

        <TracesListModal
          referenceID={profileID}
          isOpen={tracesDisclosure.isOpen}
          onClose={tracesDisclosure.onClose}
          traceTarget='Profile'
        />

        <FORoomPostingContainer
          transactionDetails={{
            folioUuid: profileID || '',
            folioStatus: '',
          }}
          onCloseModal={() => {
            roomPostingDisclosure.onClose();
          }}
          isOpenModal={roomPostingDisclosure.isOpen}
          transactionType={'Post Master'}
        />
        <DialogInformation />
      </>
    </FormProvider>
  );
};

export default memo(FOProfileGroupDrawer);
